import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useGamesBase } from '../../utils/gamesBase';
import FitToWidth from '../../components/Games2/FitToWidth';
import { XMarkIcon, EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import apiClient from '../../utils/apiClient';
import { useAuth } from '../../contexts/AuthContext';

// ============== Cube Rotation Logic ==============
// Faces: top, front, bottom, back, left, right
// Commands: front, back, left, right (rotation directions)

// Rotation mappings: given current face, applying a command yields a new face
// "front" = tilt cube forward (top goes to front, front goes to bottom, etc.)
// "back" = tilt cube backward (top goes to back, back goes to bottom, etc.)
// "left" = rotate cube left (top goes to left, left goes to bottom, etc.)
// "right" = rotate cube right (top goes to right, right goes to bottom, etc.)
const ROTATION_MAP = {
  front: { top: 'front', front: 'bottom', bottom: 'back', back: 'top', left: 'left', right: 'right' },
  back:  { top: 'back', back: 'bottom', bottom: 'front', front: 'top', left: 'left', right: 'right' },
  left:  { top: 'left', left: 'bottom', bottom: 'right', right: 'top', front: 'front', back: 'back' },
  right: { top: 'right', right: 'bottom', bottom: 'left', left: 'top', front: 'front', back: 'back' },
};

const FACES = ['top', 'front', 'bottom', 'back', 'left', 'right'];
const COMMANDS = ['front', 'back', 'left', 'right'];

const applyCommand = (currentFace, command) => {
  return ROTATION_MAP[command][currentFace];
};

const applyCommands = (initialFace, commands) => {
  let face = initialFace;
  for (const cmd of commands) {
    face = applyCommand(face, cmd);
  }
  return face;
};

// ============== Audio Playback (iOS-compatible Web Audio API) ==============
let audioCtx = null;
const audioBuffers = {};
let audioLoaded = false;
let activeSource = null;

const ensureAudioContext = () => {
  if (!audioCtx) {
    audioCtx = new (window.AudioContext || window.webkitAudioContext)();
  }
  // Critical for iOS: resume if suspended
  if (audioCtx.state === 'suspended') {
    audioCtx.resume();
  }
  return audioCtx;
};

// Preload all audio files into AudioBuffers
const loadAllAudioBuffers = async () => {
  if (audioLoaded) return;
  const ctx = ensureAudioContext();
  
  const audioMap = {
    'initial position': 'Initial-position.wav',
    'front': 'Front.wav',
    'back': 'Back.wav',
    'left': 'Left.wav',
    'right': 'Right.wav',
    'top': 'Top.wav',
    'bottom': 'Bottom.wav'
  };
  
  const loadPromises = Object.entries(audioMap).map(([key, fileName]) =>
    fetch(`/audio/rot/${fileName}`)
      .then(res => res.arrayBuffer())
      .then(buf => ctx.decodeAudioData(buf))
      .then(decoded => { audioBuffers[key] = decoded; })
      .catch(err => console.error(`Failed to load ${fileName}:`, err))
  );
  
  await Promise.all(loadPromises);
  audioLoaded = true;
};

const warmUpSpeech = () => {
  // Initialize audio context on user interaction (iOS requirement)
  ensureAudioContext();
  loadAllAudioBuffers();
};

// durationScale < 1 resolves slightly before the clip fully ends, so the next
// command starts sooner (trims trailing tail). This raises cadence without ever
// touching playbackRate, so pitch stays identical across devices and speed modes.
const speak = (text, durationScale = 1.0) => {
  return new Promise((resolve) => {
    const ctx = ensureAudioContext();
    const buffer = audioBuffers[text.toLowerCase()];

    if (!buffer) {
      console.warn(`No audio buffer for: ${text}`);
      resolve();
      return;
    }

    // Stop any currently playing audio
    if (activeSource) {
      try { activeSource.stop(); } catch (e) { /* already stopped */ }
      activeSource = null;
    }

    const source = ctx.createBufferSource();
    source.buffer = buffer;
    // Always play at natural rate: keeps pitch identical across every device and
    // both speed modes. Speed is controlled solely by the inter-command gap below.
    source.playbackRate.value = 1.0;
    source.connect(ctx.destination);
    activeSource = source;

    try {
      source.start(0);
    } catch (e) {
      console.error('Failed to start audio:', e);
      resolve();
      return;
    }

    // Resolve on the clip's true length. buffer.duration is the file's real duration
    // (independent of the device's AudioContext sample rate), so the cadence is
    // identical on every device/browser. setTimeout is used instead of onended,
    // which fires with device-dependent latency (notably on iOS).
    const durationMs = Math.ceil(buffer.duration * durationScale * 1000);
    setTimeout(() => {
      activeSource = null;
      resolve();
    }, durationMs);
  });
};

const stopSpeech = () => {
  if (activeSource) {
    try { activeSource.stop(); } catch (e) { /* already stopped */ }
    activeSource = null;
  }
};

// ============== Speed Config ==============
// Speed is expressed purely as the pause between commands (ms). Speech itself always
// plays at natural rate/pitch (see speak()), so slow vs fast is device-independent.
const SPEED_CONFIG = {
  slow:   { commandGap: 1200, durationScale: 1.0 },
  fast:   { commandGap: 0,    durationScale: 1.0 },
  // Level 3: slightly quicker than fast by trimming each clip's trailing tail.
  // Pitch is unchanged (still natural playbackRate).
  fastL3: { commandGap: 0,    durationScale: 0.9 },
};

// ============== Level Config ==============
const LEVEL_CONFIG = {
  1: { totalRounds: 20, speedPattern: 'all_slow', pauseAfterInitial: true, feedback: false, scoreMultiplier: 0.6 },
  2: { totalRounds: 35, speedPattern: 'slow_then_fast', pauseAfterInitial: true, feedback: true, scoreMultiplier: 0.8 },
  3: { totalRounds: 35, speedPattern: 'all_fast', pauseAfterInitial: false, feedback: false, scoreMultiplier: 1.0 },
};

const generateRound = () => {
  const initialFace = FACES[Math.floor(Math.random() * FACES.length)];
  const commandCount = Math.floor(Math.random() * 4) + 6; // 6-9 commands
  const commands = [];
  for (let i = 0; i < commandCount; i++) {
    commands.push(COMMANDS[Math.floor(Math.random() * COMMANDS.length)]);
  }
  const correctAnswer = applyCommands(initialFace, commands);
  return { initialFace, commands, correctAnswer };
};

// ============== Cube Face SVG Components ==============
// Each face shows a 3D cube with an X on the marked face
const CubeFaceOption = ({ face, selected, correct, showResult, onClick }) => {
  const isCorrect = showResult && face === correct;
  const isWrong = showResult && face === selected && face !== correct;
  const isSelected = !showResult && face === selected;
  
  let borderColor = 'border-gray-300 dark:border-gray-600';
  let bgColor = 'bg-white dark:bg-gray-800';
  if (isCorrect) {
    borderColor = 'border-green-500';
    bgColor = 'bg-green-50 dark:bg-green-900/30';
  } else if (isWrong) {
    borderColor = 'border-red-500';
    bgColor = 'bg-red-50 dark:bg-red-900/30';
  } else if (isSelected) {
    borderColor = 'border-cyan-500';
    bgColor = 'bg-cyan-50 dark:bg-cyan-900/20';
  }

  // Simple 3D cube representation with X marking
  const getCubeGraphic = (f) => {
    // Cube vertices for isometric view
    const cubePoints = {
      // Front face
      fl: [20, 55], ft: [20, 20], ftr: [65, 20], fr: [65, 55],
      // Back face (offset)
      bl: [35, 42], bt: [35, 7], btr: [80, 7], br: [80, 42],
    };
    
    const p = cubePoints;
    
    // Draw X on the appropriate face
    const getXLines = (targetFace) => {
      switch(targetFace) {
        case 'front':
          return (
            <>
              <line x1={p.fl[0]} y1={p.fl[1]} x2={p.ftr[0]} y2={p.ft[1]} stroke="red" strokeWidth="2.5"/>
              <line x1={p.ft[0]} y1={p.ft[1]} x2={p.fr[0]} y2={p.fr[1]} stroke="red" strokeWidth="2.5"/>
            </>
          );
        case 'back':
          return (
            <>
              <line x1={p.bl[0]+15} y1={p.bt[1]} x2={p.btr[0]} y2={p.br[1]-3} stroke="red" strokeWidth="2.5" strokeDasharray="4,2"/>
              <line x1={p.btr[0]} y1={p.bt[1]} x2={p.bl[0]+15} y2={p.br[1]-3} stroke="red" strokeWidth="2.5" strokeDasharray="4,2"/>
            </>
          );
        case 'top':
          return (
            <>
              <line x1={p.ft[0]} y1={p.ft[1]} x2={p.btr[0]} y2={p.bt[1]} stroke="red" strokeWidth="2.5"/>
              <line x1={p.ftr[0]} y1={p.ft[1]} x2={p.bl[0]} y2={p.bt[1]} stroke="red" strokeWidth="2.5"/>
            </>
          );
        case 'bottom':
          return (
            <>
              <line x1={p.fl[0]} y1={p.fl[1]} x2={p.br[0]} y2={p.br[1]} stroke="red" strokeWidth="2.5" strokeDasharray="4,2"/>
              <line x1={p.fr[0]} y1={p.fr[1]} x2={p.bl[0]} y2={p.bl[1]} stroke="red" strokeWidth="2.5" strokeDasharray="4,2"/>
            </>
          );
        case 'right':
          return (
            <>
              <line x1={p.ftr[0]} y1={p.ft[1]} x2={p.br[0]} y2={p.br[1]} stroke="red" strokeWidth="2.5"/>
              <line x1={p.fr[0]} y1={p.fr[1]} x2={p.btr[0]} y2={p.bt[1]} stroke="red" strokeWidth="2.5"/>
            </>
          );
        case 'left':
          return (
            <>
              <line x1={p.fl[0]} y1={p.fl[1]} x2={p.bt[0]} y2={p.bt[1]} stroke="red" strokeWidth="2.5"/>
              <line x1={p.ft[0]} y1={p.ft[1]} x2={p.bl[0]} y2={p.bl[1]} stroke="red" strokeWidth="2.5"/>
            </>
          );
        default:
          return null;
      }
    };

    return (
      <svg viewBox="0 0 100 65" className="w-full h-full">
        {/* Back face */}
        <polygon points={`${p.bl[0]},${p.bl[1]} ${p.bt[0]},${p.bt[1]} ${p.btr[0]},${p.bt[1]} ${p.br[0]},${p.br[1]}`} 
          fill="none" stroke="#666" strokeWidth="1.5" strokeDasharray="3,2"/>
        {/* Front face */}
        <polygon points={`${p.fl[0]},${p.fl[1]} ${p.ft[0]},${p.ft[1]} ${p.ftr[0]},${p.ft[1]} ${p.fr[0]},${p.fr[1]}`} 
          fill="none" stroke="#333" strokeWidth="2"/>
        {/* Connecting edges */}
        <line x1={p.fl[0]} y1={p.fl[1]} x2={p.bl[0]} y2={p.bl[1]} stroke="#555" strokeWidth="1.5"/>
        <line x1={p.ft[0]} y1={p.ft[1]} x2={p.bt[0]} y2={p.bt[1]} stroke="#555" strokeWidth="1.5"/>
        <line x1={p.ftr[0]} y1={p.ftr[1]} x2={p.btr[0]} y2={p.btr[1]} stroke="#555" strokeWidth="1.5"/>
        <line x1={p.fr[0]} y1={p.fr[1]} x2={p.br[0]} y2={p.br[1]} stroke="#555" strokeWidth="1.5"/>
        {/* Top face outline */}
        <polygon points={`${p.ft[0]},${p.ft[1]} ${p.bt[0]},${p.bt[1]} ${p.btr[0]},${p.bt[1]} ${p.ftr[0]},${p.ft[1]}`} 
          fill="none" stroke="#444" strokeWidth="1.5"/>
        {/* Right face outline */}
        <polygon points={`${p.ftr[0]},${p.ftr[1]} ${p.btr[0]},${p.btr[1]} ${p.br[0]},${p.br[1]} ${p.fr[0]},${p.fr[1]}`} 
          fill="none" stroke="#444" strokeWidth="1.5"/>
        {/* X marking */}
        {getXLines(f)}
      </svg>
    );
  };

  return (
    <button
      onClick={onClick}
      disabled={showResult}
      className={`flex flex-col items-center p-3 rounded-xl border-2 transition-all ${borderColor} ${bgColor} ${
        !showResult ? 'hover:border-cyan-400 hover:shadow-lg cursor-pointer' : ''
      }`}
    >
      <div className="w-24 h-16 mb-2">
        {getCubeGraphic(face)}
      </div>
      <span className={`text-sm font-medium capitalize ${
        isCorrect ? 'text-green-600' : isWrong ? 'text-red-600' : 'text-gray-700 dark:text-gray-300'
      }`}>
        {face}
      </span>
    </button>
  );
};

// ============== Unfolded Cube Display ==============
const UnfoldedCube = ({ highlightedFace, size = 200 }) => {
  const cellSize = size / 4;
  
  // Cross layout positions (col, row) for each face
  const positions = {
    back:   { col: 1, row: 0 },
    left:   { col: 0, row: 1 },
    top:    { col: 1, row: 1 },
    right:  { col: 2, row: 1 },
    front:  { col: 1, row: 2 },
    bottom: { col: 1, row: 3 },
  };

  return (
    <svg width={cellSize * 3} height={cellSize * 4} className="mx-auto">
      {FACES.map(face => {
        const pos = positions[face];
        const x = pos.col * cellSize;
        const y = pos.row * cellSize;
        const isHighlighted = face === highlightedFace;
        
        return (
          <g key={face}>
            <rect
              x={x} y={y} width={cellSize} height={cellSize}
              fill={isHighlighted ? '#ef4444' : '#f3f4f6'}
              stroke="#374151" strokeWidth="2"
              rx="2"
            />
            {isHighlighted && (
              <>
                <line x1={x + 8} y1={y + 8} x2={x + cellSize - 8} y2={y + cellSize - 8} stroke="white" strokeWidth="3"/>
                <line x1={x + cellSize - 8} y1={y + 8} x2={x + 8} y2={y + cellSize - 8} stroke="white" strokeWidth="3"/>
              </>
            )}
            <text
              x={x + cellSize / 2} y={y + cellSize - 6}
              textAnchor="middle" fontSize="9" fill={isHighlighted ? 'white' : '#6b7280'}
              fontWeight="500"
            >
              {face}
            </text>
          </g>
        );
      })}
    </svg>
  );
};

// ============== Main Game Component ==============
const ROTGame = () => {
  // Debug overlays reveal answers; only staff may toggle them (Shift+O)
  const { user } = useAuth();
  const isStaff = !!(user?.is_staff || user?.is_superuser);
  const { subjectId } = useParams();
  const navigate = useNavigate();
  const gamesBase = useGamesBase();
  const location = useLocation();
  const settings = location.state || {};
  
  const level = settings.level || 1;
  const feedbackEnabled = settings.feedback !== undefined ? settings.feedback : false;
  const levelConfig = LEVEL_CONFIG[level] || LEVEL_CONFIG[1];
  const totalRounds = levelConfig.totalRounds;

  // Assist mode (only available in L3)
  const assistMode = settings.assist !== undefined ? settings.assist : false;
  const [assistEnabled, setAssistEnabled] = useState(assistMode);
  const [assistUsedInL3, setAssistUsedInL3] = useState(false);

  // Game state
  const [gamePhase, setGamePhase] = useState('intro'); // intro, attention, initial, listening, input, feedback, results
  const [currentRound, setCurrentRound] = useState(0);
  const [roundData, setRoundData] = useState(null);
  const [userAnswer, setUserAnswer] = useState(null);
  const [roundsData, setRoundsData] = useState([]);
  const [showExitConfirm, setShowExitConfirm] = useState(false);
  const [introCountdown, setIntroCountdown] = useState(3);
  const [currentCommandIndex, setCurrentCommandIndex] = useState(-1);
  const [showDebug, setShowDebug] = useState(false);

  const toggleAssist = useCallback(() => {
    setAssistEnabled(prev => {
      const newVal = !prev;
      if (newVal && level === 3) {
        setAssistUsedInL3(true);
      }
      return newVal;
    });
  }, [level]);

  // Refs
  const abortRef = useRef(false);
  const gameStartedRef = useRef(null);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      abortRef.current = true;
      stopSpeech();
    };
  }, []);

  // Debug toggle
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (isStaff && e.shiftKey && e.key === 'O') {
        setShowDebug(prev => !prev);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isStaff]);

  // Set game start time & warm up speech for mobile/tablet
  useEffect(() => {
    gameStartedRef.current = new Date().toISOString();
    warmUpSpeech();
  }, []);

  // Intro countdown
  useEffect(() => {
    if (gamePhase !== 'intro') return;
    
    if (introCountdown <= 0) {
      startRound();
      return;
    }
    
    const timer = setTimeout(() => {
      setIntroCountdown(prev => prev - 1);
    }, 1000);
    
    return () => clearTimeout(timer);
  }, [gamePhase, introCountdown]);

  // Get speed for current round
  const getSpeedForRound = useCallback((roundIndex) => {
    if (levelConfig.speedPattern === 'all_slow') return 'slow';
    if (levelConfig.speedPattern === 'all_fast') return 'fastL3';
    // slow_then_fast: first 15 slow, rest fast
    if (levelConfig.speedPattern === 'slow_then_fast') {
      return roundIndex < 15 ? 'slow' : 'fast';
    }
    return 'slow';
  }, [levelConfig]);

  const startRound = useCallback(async () => {
    const round = generateRound();
    setRoundData(round);
    setUserAnswer(null);
    setCurrentCommandIndex(-1);
    abortRef.current = false;

    const speed = getSpeedForRound(currentRound);
    const config = SPEED_CONFIG[speed];

    // Phase: attention
    setGamePhase('attention');
    await new Promise(r => setTimeout(r, 1500));
    if (abortRef.current) return;

    // Critical for iOS: ensure audio context is active before playing
    ensureAudioContext();

    // Phase: initial position
    setGamePhase('initial');
    await speak('initial position');
    if (abortRef.current) return;
    await new Promise(r => setTimeout(r, 300));
    if (abortRef.current) return;
    await speak(round.initialFace);
    if (abortRef.current) return;

    // Pause after initial position (levels 1 & 2)
    if (levelConfig.pauseAfterInitial) {
      await new Promise(r => setTimeout(r, 5000));
      if (abortRef.current) return;
    }

    // Phase: listening (commands)
    setGamePhase('listening');
    for (let i = 0; i < round.commands.length; i++) {
      if (abortRef.current) return;
      setCurrentCommandIndex(i);
      await speak(round.commands[i], config.durationScale);
      if (abortRef.current) return;
      if (i < round.commands.length - 1) {
        await new Promise(r => setTimeout(r, config.commandGap));
        if (abortRef.current) return;
      }
    }

    // Phase: input
    if (abortRef.current) return;
    setGamePhase('input');
  }, [currentRound, getSpeedForRound, levelConfig]);

  const handleFaceSelect = useCallback((face) => {
    if (gamePhase !== 'input' || userAnswer !== null) return;
    setUserAnswer(face);
    
    const isCorrect = face === roundData.correctAnswer;
    const speed = getSpeedForRound(currentRound);
    
    const roundResult = {
      round: currentRound + 1,
      initial_face: roundData.initialFace,
      commands: roundData.commands,
      correct_answer: roundData.correctAnswer,
      user_answer: face,
      is_correct: isCorrect,
      speed: speed,
      command_count: roundData.commands.length,
    };

    const newRoundsData = [...roundsData, roundResult];
    setRoundsData(newRoundsData);

    if (feedbackEnabled) {
      setGamePhase('feedback');
    } else {
      // No feedback - move on immediately without revealing answer
      setTimeout(() => {
        if (currentRound + 1 >= totalRounds) {
          finishGame(newRoundsData);
        } else {
          setCurrentRound(prev => prev + 1);
        }
      }, 300);
    }
  }, [gamePhase, userAnswer, roundData, currentRound, roundsData, feedbackEnabled, totalRounds, getSpeedForRound]);

  const handleContinue = useCallback(() => {
    if (currentRound + 1 >= totalRounds) {
      finishGame(roundsData);
    } else {
      // Reset state before advancing to next round
      setUserAnswer(null);
      setGamePhase('attention');
      setCurrentRound(prev => prev + 1);
    }
  }, [currentRound, totalRounds, roundsData]);

  // Start new round when currentRound changes (after intro)
  useEffect(() => {
    if (gamePhase === 'intro' || gamePhase === 'results') return;
    if (gamePhase === 'feedback') return;
    if (currentRound > 0) {
      startRound();
    }
  }, [currentRound]);

  const finishGame = async (finalRoundsData) => {
    setGamePhase('results');
    
    try {
      await apiClient.post('/api/courses/rot-game/runs/', {
        subject: subjectId,
        level: level,
        feedback_enabled: feedbackEnabled,
        score_multiplier: 1.0,
        rounds_data: finalRoundsData,
        started_at: gameStartedRef.current || new Date().toISOString(),
      });
    } catch (error) {
      console.error('Failed to save ROT game run:', error);
    }
  };

  const handleExit = () => {
    abortRef.current = true;
    stopSpeech();
    navigate(`${gamesBase}/rot/${subjectId}`);
  };

  // Calculate stats
  const correctCount = roundsData.filter(r => r.is_correct).length;
  const percentage = roundsData.length > 0 ? Math.round((correctCount / roundsData.length) * 100) : 0;

  // Keyboard shortcuts for face selection during input phase
  useEffect(() => {
    if (gamePhase !== 'input' || userAnswer !== null) return;
    
    const handleKeyDown = (e) => {
      const keyMap = { '1': 'front', '2': 'back', '3': 'left', '4': 'right', '5': 'top', '6': 'bottom' };
      if (keyMap[e.key]) {
        handleFaceSelect(keyMap[e.key]);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [gamePhase, userAnswer, handleFaceSelect]);

  // Continue on Enter/Space during feedback
  useEffect(() => {
    if (gamePhase !== 'feedback') return;
    
    const handleKeyDown = (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        handleContinue();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [gamePhase, handleContinue]);

  return (
    <div className="min-h-screen bg-gray-100 dark:bg-gray-900 flex flex-col">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 shadow-sm px-4 py-2 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <h1 className="text-sm font-semibold text-gray-900 dark:text-white">Cube Rotation</h1>
          <span className="text-xs text-gray-500">
            Round {Math.min(currentRound + 1, totalRounds)}/{totalRounds}
          </span>
          <span className="text-xs text-gray-500">
            Score: {correctCount}/{roundsData.length}
          </span>
          {(level === 3 && assistUsedInL3) && (
            <span className="text-[10px] text-amber-500 font-medium">Assist penalty: 0.8x</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={toggleAssist}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-semibold transition-all shadow-sm ${
              assistEnabled
                ? 'bg-cyan-500 text-white hover:bg-cyan-600'
                : 'bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
            }`}
            title={assistEnabled ? 'Disable visual assist' : 'Enable visual assist'}
          >
            {assistEnabled ? <EyeIcon className="h-4 w-4" /> : <EyeSlashIcon className="h-4 w-4" />}
            Assist {assistEnabled ? 'ON' : 'OFF'}
          </button>
          <button
            onClick={() => setShowExitConfirm(true)}
            className="p-1.5 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            <XMarkIcon className="h-5 w-5 text-gray-500" />
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex items-center justify-center p-4">
        {/* Intro Countdown */}
        {gamePhase === 'intro' && (
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Get Ready</h2>
            <div className="text-6xl font-bold text-cyan-500">{introCountdown}</div>
            <p className="text-sm text-gray-500 mt-4">Level {level} • {totalRounds} rounds</p>
          </div>
        )}

        {/* Attention Phase */}
        {gamePhase === 'attention' && (
          <div className="text-center">
            <h2 className="text-4xl font-bold text-amber-500 animate-pulse">Attention</h2>
          </div>
        )}

        {/* Initial Position Phase */}
        {gamePhase === 'initial' && roundData && (
          <div className="text-center">
            <h2 className="text-lg text-gray-500 dark:text-gray-400 mb-4">Initial Position</h2>
            <div className="text-4xl font-bold text-cyan-500 capitalize mb-6">{roundData.initialFace}</div>
            {assistEnabled && (
              <>
                <div className="mb-3 px-3 py-1.5 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg inline-block">
                  <p className="text-[10px] text-amber-600 dark:text-amber-400">Visual assist — not available in real test</p>
                </div>
                <FitToWidth><UnfoldedCube highlightedFace={roundData.initialFace} size={240} /></FitToWidth>
              </>
            )}
            {levelConfig.pauseAfterInitial && (
              <p className="text-xs text-gray-400 mt-4">Memorize the position...</p>
            )}
          </div>
        )}

        {/* Listening Phase */}
        {gamePhase === 'listening' && roundData && (
          <div className="text-center">
            <h2 className="text-sm text-gray-500 dark:text-gray-400 mb-2">Follow the commands</h2>
            {assistEnabled && (
              <>
                <div className="mb-3 px-3 py-1.5 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg inline-block">
                  <p className="text-[10px] text-amber-600 dark:text-amber-400">Visual assist — not available in real test</p>
                </div>
                <div className="text-5xl font-bold text-cyan-500 capitalize mb-4">
                  {currentCommandIndex >= 0 ? roundData.commands[currentCommandIndex] : '...'}
                </div>
                <div className="flex justify-center gap-1 mt-2">
                  {roundData.commands.map((_, i) => (
                    <div
                      key={i}
                      className={`w-3 h-3 rounded-full transition-colors ${
                        i < currentCommandIndex ? 'bg-gray-400' :
                        i === currentCommandIndex ? 'bg-cyan-500 scale-125' :
                        'bg-gray-200 dark:bg-gray-700'
                      }`}
                    />
                  ))}
                </div>
                <p className="text-xs text-gray-400 mt-2">
                  Command {Math.min(currentCommandIndex + 1, roundData.commands.length)}/{roundData.commands.length}
                </p>
                <div className="mt-4">
                  <UnfoldedCube
                    highlightedFace={
                      currentCommandIndex >= 0
                        ? applyCommands(roundData.initialFace, roundData.commands.slice(0, currentCommandIndex + 1))
                        : roundData.initialFace
                    }
                    size={180}
                  />
                </div>
              </>
            )}
            {!assistEnabled && (
              <div className="mt-8">
                <div className="w-16 h-16 mx-auto rounded-full bg-cyan-500/20 flex items-center justify-center animate-pulse">
                  <div className="w-8 h-8 rounded-full bg-cyan-500/40"></div>
                </div>
                <p className="text-xs text-gray-400 mt-4">Listen carefully...</p>
              </div>
            )}
          </div>
        )}

        {/* Input Phase - Select Face */}
        {(gamePhase === 'input' || gamePhase === 'feedback') && roundData && (
          <div className="text-center w-full max-w-2xl">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              {gamePhase === 'feedback' 
                ? (userAnswer === roundData.correctAnswer ? 'Correct!' : 'Incorrect')
                : 'Which face is the X on now?'}
            </h2>
            
            {gamePhase === 'feedback' && (
              <p className="text-sm text-gray-500 mb-2">
                Started on <span className="font-medium capitalize text-cyan-500">{roundData.initialFace}</span>
                {' → '}{roundData.commands.length} commands → Answer: <span className="font-medium capitalize text-green-500">{roundData.correctAnswer}</span>
              </p>
            )}

            <FitToWidth className="mt-6">
            <div className="grid grid-cols-6 gap-3" style={{ width: 640 }}>
              {FACES.map(face => (
                <CubeFaceOption
                  key={face}
                  face={face}
                  selected={userAnswer}
                  correct={roundData.correctAnswer}
                  showResult={gamePhase === 'feedback'}
                  onClick={() => handleFaceSelect(face)}
                />
              ))}
            </div>
            </FitToWidth>

            <div className="text-[10px] text-gray-400 mt-3">
              Keyboard: 1=front 2=back 3=left 4=right 5=top 6=bottom
            </div>

            {gamePhase === 'feedback' && (
              <div className="mt-6">
                <div className="mb-4">
                  <p className="text-xs text-gray-500">Commands: {roundData.commands.map(c => c).join(' → ')}</p>
                </div>
                <button
                  onClick={handleContinue}
                  className="px-8 py-2.5 bg-cyan-500 hover:bg-cyan-600 text-white rounded-lg font-medium transition-colors"
                >
                  {currentRound + 1 >= totalRounds ? 'View Results' : 'Next Round'}
                </button>
              </div>
            )}
          </div>
        )}

        {/* Results Phase */}
        {gamePhase === 'results' && (
          <div className="text-center max-w-lg">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Game Complete</h2>
            <div className="text-5xl font-bold text-cyan-500 mb-1">{percentage}%</div>
            <p className="text-sm text-gray-500 mb-2">{correctCount}/{roundsData.length} correct answers</p>
            {(level === 3 && assistUsedInL3) && (
              <div className="mb-4 px-4 py-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg inline-block">
                <p className="text-xs text-amber-600 dark:text-amber-400 font-medium">
                  ⚠ Assist was used — score multiplier reduced to 0.8x
                </p>
              </div>
            )}
            <div className="mb-6"></div>
            
            {/* Round-by-round results */}
            <div className="bg-white dark:bg-gray-800 rounded-xl p-4 mb-6 shadow">
              <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">Round Details</h3>
              <div className="grid grid-cols-7 gap-1.5">
                {roundsData.map((r, i) => (
                  <div
                    key={i}
                    className={`rounded p-1.5 text-center ${
                      r.is_correct ? 'bg-green-50 dark:bg-green-900/20' : 'bg-red-50 dark:bg-red-900/20'
                    }`}
                  >
                    <p className="text-[9px] text-gray-400">R{i + 1}</p>
                    <p className={`text-sm font-bold ${r.is_correct ? 'text-green-600' : 'text-red-600'}`}>
                      {r.is_correct ? '✓' : '✗'}
                    </p>
                    <p className="text-[8px] text-gray-400">{r.command_count}c</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex gap-3 justify-center">
              <button
                onClick={() => navigate(`${gamesBase}/rot/${subjectId}/play`, { state: settings })}
                className="px-8 py-3 bg-cyan-500 hover:bg-cyan-600 text-white rounded-lg font-medium transition-colors"
              >
                Play Again
              </button>
              <button
                onClick={() => navigate(`${gamesBase}/rot/${subjectId}`)}
                className="px-8 py-3 bg-gray-800 hover:bg-gray-900 text-white rounded-lg font-medium transition-colors"
              >
                Back to Setup
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Debug Overlay */}
      {showDebug && (
        <div className="fixed top-12 left-4 bg-black/80 text-green-400 p-4 rounded-lg text-xs font-mono z-50 max-w-md">
          <p className="font-bold mb-2 text-white">DEBUG (Shift+O to toggle)</p>
          <p>Phase: {gamePhase}</p>
          <p>Round: {currentRound + 1}/{totalRounds}</p>
          <p>Level: {level} | Speed: {getSpeedForRound(currentRound)}</p>
          <p>Feedback: {feedbackEnabled ? 'ON' : 'OFF'}</p>
          <p>Pause after initial: {levelConfig.pauseAfterInitial ? 'YES' : 'NO'}</p>
          <p>Assist: {assistEnabled ? 'ON' : 'OFF'} {assistUsedInL3 ? '(used in L3!)' : ''}</p>
          {roundData && (
            <>
              <p className="mt-2 text-cyan-400 font-bold">Current Round</p>
              <p>Initial: <span className="text-white capitalize">{roundData.initialFace}</span></p>
              <p>Commands: <span className="text-white">{roundData.commands.join(', ')}</span></p>
              <p>Correct: <span className="text-white capitalize">{roundData.correctAnswer}</span></p>
              <p>User answer: <span className="text-white capitalize">{userAnswer || '-'}</span></p>
            </>
          )}
          <p className="mt-2 text-yellow-400">Score: {correctCount}/{roundsData.length} ({percentage}%)</p>
        </div>
      )}

      {/* Exit Confirmation Modal */}
      {showExitConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 max-w-sm mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Exit Game?</h3>
            <p className="text-gray-600 mb-4">Your progress will not be saved.</p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowExitConfirm(false)}
                className="flex-1 py-2.5 px-4 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-lg font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleExit}
                className="flex-1 py-2.5 px-4 bg-red-500 hover:bg-red-600 text-white rounded-lg font-medium transition-colors"
              >
                Exit
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ROTGame;
